import { useEffect, useRef, useState, useCallback } from "react";
import { ShareExpiry } from "@/components/ShareExpiry";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import ImageAnnotator, { AnnotationLayer } from "@/components/ImageAnnotator";
import { ArrowLeft, ArrowUp, ArrowDown, ListChecks, CheckCircle2, Trash2, Plus, Upload, Pencil, Check, CloudUpload, Loader2, SlidersHorizontal, Share2, Copy, RefreshCw, ExternalLink, Camera, HardDrive, Printer } from "lucide-react";
import { useProdLang } from "@/i18n/useProdLang";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NONE = "__none__";
const DEPT_ROUTE = { svejsning: "/produktion/svejsning", montage: "/produktion/montage", slibning: "/produktion/slibning" };
const genId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const daDate = (s) => { const d = String(s || "").slice(0, 10); if (d.length === 10) { const [y, m, dd] = d.split("-"); return `${dd}-${m}-${y}`; } return "—"; };
const LEVELS = [
  { key: "Mindre", color: "#A8A29E" },
  { key: "Væsentlig", color: "#A08979" },
  { key: "Kritisk", color: "#A83232" },
];
const STATUSES = ["Kladde", "I gang", "Færdig"];
const STYKLISTE_STATUS = "Indført på stykliste";
const STATUS_TONE = { "Færdig": "#7E856C", "I gang": "#A08979", Kladde: "#78716C", "Indført på stykliste": "#A83232" };

export default function DocumentBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const { lang, setLang, t, dir } = useProdLang();
  const [report, setReport] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [saveState, setSaveState] = useState("saved");
  const [editImg, setEditImg] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [revNote, setRevNote] = useState("");
  const [verNotice, setVerNotice] = useState(false);
  const timer = useRef(null);
  const driveInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const canEdit = report?.can_edit;

  useEffect(() => {
    api.get(`/quality-reports/${id}`).then((r) => setReport(r.data)).catch(() => { toast.error(t("toast_doc_not_found")); nav("/produktion/svejsning"); });
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
    api.get(`/quality-reports/${id}/version-notice`).then((r) => { if (r.data?.show) setVerNotice(true); }).catch(() => {});
  }, [id, nav]); // eslint-disable-line

  const ackVerNotice = () => { setVerNotice(false); api.post(`/quality-reports/${id}/version-notice/ack`).catch(() => {}); };

  const pending = useRef({});
  const inflight = useRef(false);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    if (inflight.current) return;
    const patch = pending.current;
    if (!Object.keys(patch).length) { setSaveState("saved"); return; }
    pending.current = {};
    inflight.current = true;
    setSaveState("saving");
    api.patch(`/quality-reports/${id}`, patch)
      .catch((e) => { pending.current = { ...patch, ...pending.current }; toast.error(t("toast_save_failed"), { description: formatApiError(e.response?.data?.detail) }); })
      .finally(() => { inflight.current = false; if (Object.keys(pending.current).length) flush(); else setSaveState("saved"); });
  }, [id]); // eslint-disable-line

  const schedule = useCallback((patch) => {
    pending.current = { ...pending.current, ...patch };
    setSaveState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  }, [flush]);

  const commit = useCallback((patch) => {
    pending.current = { ...pending.current, ...patch };
    flush();
  }, [flush]);

  const setMeta = (k, v) => { setReport((r) => ({ ...r, [k]: v })); schedule({ [k]: v }); };
  const TR_FIELDS = ["title", "description", "conclusion_note"];
  const isDok = !!report && (["svejsning", "montage", "slibning"].includes(report.type) || report.module === "dokumenter");
  const trActive = isDok && lang !== "da";
  const fieldVal = (k) => trActive ? ((report?.tr?.[lang]?.[k]) ?? "") : (report?.[k] || "");
  const setField = (k, v) => {
    if (!trActive) { setMeta(k, v); return; }
    setReport((r) => ({ ...r, tr: { ...(r.tr || {}), [lang]: { ...(r.tr?.[lang] || {}), [k]: v } } }));
    schedule({ [k]: v, edit_lang: lang });
  };
  const setDocImages = (updater) => setReport((r) => { const arr = typeof updater === "function" ? updater(r.doc_images || []) : updater; schedule({ doc_images: arr }); return { ...r, doc_images: arr }; });
  const setDocDefects = (defects) => { setReport((r) => ({ ...r, doc_defects: defects })); schedule({ doc_defects: defects }); };
  const updateDocImage = (iid, patch) => setDocImages((imgs) => imgs.map((im) => im.id === iid ? { ...im, ...patch } : im));
  const removeDocImage = (iid) => setDocImages((imgs) => imgs.filter((im) => im.id !== iid));
  const moveDocImage = (iid, dir) => setDocImages((imgs) => {
    const i = imgs.findIndex((im) => im.id === iid);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= imgs.length) return imgs;
    const next = [...imgs];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const dragFrom = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const reorderDocImages = (from, to) => setDocImages((imgs) => {
    if (from == null || to == null || from === to || from < 0 || to < 0 || from >= imgs.length || to >= imgs.length) return imgs;
    const next = [...imgs];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  });

  const onCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    setReport((r) => ({ ...r, customer_id: cid, customer_name: c?.name || "", customer_contact: null }));
    commit({ customer_id: cid, customer_name: c?.name || "", customer_contact: { name: "", email: "", phone: "" } });
  };

  const uploadDocImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/quality-reports/${id}/images`, fd);
      const newIds = data.ids || (data.id ? [data.id] : []);
      setDocImages((imgs) => [...imgs, ...newIds.map((iid) => ({ id: genId(), image_id: iid, caption: "", annotations: [] }))]);
      toast.success(newIds.length > 1 ? `${newIds.length} sider tilføjet` : t("toast_img_uploaded"));
    } catch (e) { toast.error(t("toast_upload_failed"), { description: formatApiError(e.response?.data?.detail) }); }
  };

  const doDelete = () => api.delete(`/quality-reports/${id}`).then(() => { toast.success(t("toast_doc_deleted")); nav(DEPT_ROUTE[report?.type] || "/produktion/svejsning"); });

  const bumpRevision = () => api.post(`/quality-reports/${id}/revision`, { note: revNote.trim() }).then((r) => {
    setReport((prev) => ({ ...prev, revision: r.data.revision, revision_date: r.data.revision_date, responsible_name: r.data.responsible_name, revision_history: [...(prev.revision_history || []), r.data.entry] }));
    toast.success(`${t("version_created_pre")}${r.data.revision}${t("version_created_post")}`, { description: t("hist_updated") });
    setRevOpen(false); setRevNote("");
  }).catch((e) => toast.error(t("toast_version_err"), { description: formatApiError(e.response?.data?.detail) }));

  const shareUrl = report?.share_token ? `${window.location.origin}/kvalitetsrapport-kunde/${report.share_token}` : "";
  const createShare = () => api.post(`/quality-reports/${id}/share`).then((r) => setReport((p) => ({ ...p, share_token: r.data.share_token }))).catch(() => toast.error(t("toast_link_create_err")));
  const regenShare = () => api.post(`/quality-reports/${id}/share/regenerate`).then((r) => { setReport((p) => ({ ...p, share_token: r.data.share_token })); toast.success(t("toast_new_link_gen")); }).catch(() => toast.error(t("toast_link_regen_err")));
  const deleteShare = () => api.delete(`/quality-reports/${id}/share`).then(() => { setReport((p) => ({ ...p, share_token: null })); toast.success(t("toast_link_deactivated")); }).catch(() => toast.error(t("toast_link_deact_err")));
  const copyShare = () => { navigator.clipboard?.writeText(shareUrl); toast.success(t("toast_link_copied")); };
  const openShare = () => { setShareOpen(true); if (!report.share_token) createShare(); };

  if (!report) return <div className="text-stone-400 text-sm py-20 text-center">{t("loading")}</div>;
  const typeLabel = t(`type_${report.type}`);
  const imgUrl = (imageId) => `${BACKEND}/api/quality-reports/${id}/images/${imageId}`;
  const editingImage = (report.doc_images || []).find((im) => im.id === editImg);
  const ro = !canEdit;
  const backRoute = DEPT_ROUTE[report.type] || "/produktion/svejsning";
  const chooseStatus = (s) => {
    setReport((r) => ({ ...r, status: s }));
    if (s === "Færdig" || s === STYKLISTE_STATUS) {
      const patch = { ...pending.current, status: s }; pending.current = {};
      api.patch(`/quality-reports/${id}`, patch).then(() => { toast.success(`Status sat til ${s}`); nav(backRoute); })
        .catch((e) => toast.error(t("toast_save_failed") || "Kunne ikke gemme", { description: formatApiError(e.response?.data?.detail) }));
      return;
    }
    commit({ status: s });
  };

  const renderPanel = (pfx = "") => (
    <>
      <div className="pcard p-5 space-y-4">
        <p className="label-caps">{t("details")}</p>
        <Field label={t("type")}>
          <div data-testid={`${pfx}doc-type`} className="rounded-xl bg-[#F3F1EC] border border-[#EBE8E3] px-3 h-10 flex items-center text-sm font-medium text-[#1A1919]">{typeLabel}</div>
        </Field>
        {report.doc_no && (
          <Field label={t("docno")}>
            <button type="button" dir="ltr" data-testid={`${pfx}doc-no`} onClick={() => { navigator.clipboard?.writeText(report.doc_no); toast.success(t("copied")); }} title={t("copy")} className="w-full rounded-xl bg-[#F3F1EC] border border-[#EBE8E3] px-3 h-10 flex items-center justify-between gap-2 text-sm font-mono font-semibold text-[#1A1919] hover:bg-[#ECEAE5] [transition-property:background-color] duration-200">
              <span>{report.doc_no}</span>
              <Copy className="h-3.5 w-3.5 text-stone-400" strokeWidth={2} />
            </button>
          </Field>
        )}
        <Field label={t("varenr")}><Input value={report.item_no || ""} disabled={ro} onChange={(e) => setMeta("item_no", e.target.value)} placeholder="fx 13990" className="rounded-xl bg-white border-[#EBE8E3]" data-testid={`${pfx}doc-item-no`} /></Field>
        <Field label={t("customer")}>
          <Select value={report.customer_id || NONE} disabled={ro} onValueChange={(v) => onCustomer(v === NONE ? "" : v)}>
            <SelectTrigger data-testid={`${pfx}doc-customer`} className="rounded-xl bg-white border-[#EBE8E3]"><SelectValue placeholder={t("no_customer")} /></SelectTrigger>
            <SelectContent><SelectItem value={NONE}>{t("no_customer")}</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      <div className="pcard p-5 space-y-3">
        <p className="label-caps">{t("status_label")}</p>
        {(() => {
          const isMathias = !!report.can_set_stykliste;
          const showStyk = isMathias || report.status === STYKLISTE_STATUS;
          const statusList = showStyk ? [...STATUSES, STYKLISTE_STATUS] : STATUSES;
          return (
          <div className={`grid gap-2 ${statusList.length > 3 ? "grid-cols-2" : "grid-cols-3"}`}>
          {statusList.map((s) => {
            const active = report.status === s;
            const tone = STATUS_TONE[s] || "#78716C";
            const styklisteLock = !isMathias && s === STYKLISTE_STATUS;
            return (
              <button key={s} data-testid={`${pfx}doc-status-${s}`} disabled={ro || styklisteLock} onClick={() => chooseStatus(s)}
                className={`py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${active ? "text-white border-transparent shadow-sm" : "bg-white text-stone-600 border-[#EBE8E3] hover:bg-[#F3F1EC]"}`}
                style={active ? { background: tone } : undefined}>{t(`status_${s}`)}</button>
            );
          })}
          </div>
          );
        })()}
      </div>

      <div className="pcard p-5 space-y-3">
        <p className="label-caps">{t("version_control")}</p>
        <div>
          <p className="text-2xl font-light text-[#1A1919] leading-none" data-testid={`${pfx}doc-revision`}>{report.revision || 1}</p>
          <p className="text-xs text-stone-400 mt-2">{t("created_on")} {daDate(report.revision_date || report.created_at)}</p>
          <p className="text-xs text-stone-400">{t("responsible")}: {report.responsible_name || report.created_by_name || "—"}</p>
        </div>
        {(report.revision_history || []).length > 0 && (
          <div className="pt-3 border-t border-[#F3F1EC] space-y-2.5" data-testid={`${pfx}doc-history`}>
            <p className="label-caps">{t("history")}</p>
            {[...report.revision_history].reverse().map((h, i) => (
              <div key={i} className="text-xs">
                <p className="font-semibold text-stone-600">v{h.version} · {daDate(h.at)}</p>
                <p className="text-stone-500 leading-snug">{h.note || t("no_description")}{h.by ? ` — ${h.by}` : ""}</p>
              </div>
            ))}
          </div>
        )}
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => setRevOpen(true)} data-testid={`${pfx}doc-new-revision`}
            className="w-full rounded-xl border-[#EBE8E3] text-stone-700 hover:bg-[#F3F1EC]">
            <Plus className="h-4 w-4 mr-1.5" /> {t("new_version")}
          </Button>
        )}
      </div>
    </>
  );

  return (
    <div className="rise-in" dir={dir}>
      <div className="no-print lg:sticky lg:top-0 z-30 -mx-5 sm:-mx-8 lg:-mx-12 mb-8 px-5 sm:px-8 lg:px-12 min-h-[64px] py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 bg-[#FBFAF8]/85 backdrop-blur-xl border-b border-[#EBE8E3]">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => nav(backRoute)} className="-ml-2 text-stone-500 hover:text-stone-800 rounded-xl shrink-0" data-testid="doc-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> {t("back")}
          </Button>
          <span className="hidden sm:block h-5 w-px bg-[#EBE8E3]" />
          <span className="hidden sm:inline text-sm text-stone-400 truncate">{t("document_word")} · {typeLabel}</span>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2" dir="ltr">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="doc-print-btn" className="rounded-xl border-[#EBE8E3] text-stone-700 hover:bg-[#F3F1EC]"><Printer className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{t("print_report")}</span></Button>
          <LanguageSwitcher lang={lang} setLang={setLang} />
          <span data-testid="doc-save-state" className="text-xs text-stone-400 inline-flex items-center gap-1.5 px-1.5">
            {saveState === "saving" ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("saving")}</> : saveState === "dirty" ? <><CloudUpload className="h-3.5 w-3.5" /> {t("changes")}</> : <><Check className="h-3.5 w-3.5 text-[#7E856C]" /> {t("saved")}</>}
          </span>
          {canEdit && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="lg:hidden rounded-xl border-[#EBE8E3] text-stone-700" data-testid="doc-panel-trigger"><SlidersHorizontal className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{t("settings")}</span></Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[360px] max-w-[90vw] overflow-y-auto bg-[#F3F1EC] p-5 space-y-4" data-testid="doc-panel-sheet" dir={dir}>
                <p className="label-caps">{t("tools")}</p>
                {renderPanel("m-")}
              </SheetContent>
            </Sheet>
          )}
          {canEdit && <Button variant="outline" size="sm" onClick={openShare} data-testid="doc-share-btn" className="rounded-xl border-[#EBE8E3] text-stone-700 hover:bg-[#F3F1EC]"><Share2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{t("share_btn")}</span></Button>}
          {canEdit && <Button variant="ghost" size="icon" onClick={() => setConfirmDel(true)} data-testid="doc-delete-btn" className="rounded-xl text-[#A83232] hover:bg-[#F1DEDA]"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8 items-start">
        <div className="pcard p-6 sm:p-10 lg:p-14 rounded-2xl max-w-[880px] w-full mx-auto lg:mx-0 min-w-0 print-area" data-testid="doc-canvas">
          <div className="flex items-start justify-between gap-4">
            <img src="/bsm-logo.png" alt="BSM A/S" className="h-9 w-auto" />
            <div className="flex flex-col items-end gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-400">{typeLabel || t("doc_type_fallback")}</span>
              <ConclusionBadge report={report} t={t} />
            </div>
          </div>
          <div className="mt-8 h-px bg-gradient-to-r from-[#A83232]/50 via-[#EBE8E3] to-transparent" />
          <input data-testid="doc-title" value={fieldVal("title")} disabled={ro}
            onChange={(e) => setField("title", e.target.value)}
            className={`w-full mt-7 text-3xl sm:text-4xl font-semibold text-[#1A1919] bg-transparent outline-none placeholder:text-stone-300 placeholder:font-light ${dir === "rtl" ? "" : "tracking-tight"}`}
            placeholder={t("title_ph")} />
          {trActive && <p className="mt-2 text-xs" style={{ color: "#806B61" }} data-testid="doc-autotranslate-hint">{t("autotranslate_hint")}</p>}
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-5 pb-8 mb-8 border-b border-[#EBE8E3]" data-testid="doc-meta-line">
            <MetaItem label={t("varenr")} value={report.item_no} />
            <MetaItem label={t("customer")} value={report.customer_name} />
            <MetaItem label={t("date")} value={daDate(report.revision_date || report.created_at)} />
            <MetaItem label={t("responsible")} value={report.responsible_name || report.created_by_name} />
            <MetaItem label={t("version_label")} value={`${report.revision || 1}`} />
          </div>

          <div className="space-y-12">
            <div className="rounded-xl border border-[#E9C9C3] bg-[#F1DEDA]/60 px-5 py-4" data-testid="doc-fixed-note">
              <p className="text-sm text-[#1A1919] leading-relaxed">{t("bom_pre")}<strong className="font-semibold">{t("bom_strong")}</strong>{t("bom_post")}</p>
            </div>
            <section data-testid="doc-section-beskrivelse">
              <h3 className="text-lg font-semibold text-[#1A1919] mb-3">{t("description")}</h3>
              <Textarea value={fieldVal("description")} disabled={ro} onChange={(e) => setField("description", e.target.value)} placeholder={t("write_description")} className="border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[90px]" data-testid="doc-description" />
            </section>

            <section data-testid="doc-section-dokumentation">
              <h3 className="text-lg font-semibold text-[#1A1919] mb-3">{t("documentation")}</h3>
              <div className="space-y-4">
                {(report.doc_images || []).map((im, idx, arr) => (
                  <div key={im.id} data-testid="doc-doc-image"
                    onDragOver={canEdit ? (e) => { e.preventDefault(); if (dragOverIdx !== idx) setDragOverIdx(idx); } : undefined}
                    onDrop={canEdit ? () => { reorderDocImages(dragFrom.current, idx); dragFrom.current = null; setDragOverIdx(null); } : undefined}
                    className={`print-avoid rounded-2xl [transition-property:box-shadow] duration-150 ${dragOverIdx === idx ? "ring-2 ring-[#A83232] ring-offset-2" : ""}`}>
                    <div className="relative rounded-xl overflow-hidden border border-[#EBE8E3] min-h-[120px] bg-[#F3F1EC]"
                      draggable={canEdit}
                      onDragStart={canEdit ? (e) => { dragFrom.current = idx; e.dataTransfer.effectAllowed = "move"; } : undefined}
                      onDragEnd={() => { dragFrom.current = null; setDragOverIdx(null); }}
                      style={canEdit ? { cursor: "move" } : undefined}>
                      <img src={imgUrl(im.image_id)} alt="" className="block w-full h-auto min-h-[120px] object-contain" />
                      <AnnotationLayer annotations={im.annotations || []} />
                      {canEdit && arr.length > 1 && (
                        <div className="no-print absolute top-2 left-2 flex flex-col gap-1.5">
                          <button onClick={() => moveDocImage(im.id, -1)} disabled={idx === 0} data-testid="doc-move-up"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/95 border border-[#EBE8E3] text-stone-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button onClick={() => moveDocImage(im.id, 1)} disabled={idx === arr.length - 1} data-testid="doc-move-down"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/95 border border-[#EBE8E3] text-stone-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                            <ArrowDown className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      {canEdit && (
                        <div className="no-print absolute top-2 right-2 flex items-center gap-2">
                          <button onClick={() => setEditImg(im.id)} data-testid="doc-edit-image"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/95 border border-[#EBE8E3] text-stone-700 hover:bg-white">
                            <Pencil className="h-3.5 w-3.5" /> {t("edit_and_mark")}
                          </button>
                          <button onClick={() => removeDocImage(im.id)} data-testid="doc-remove-image"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/95 border border-[#EBE8E3] text-[#A83232] hover:bg-[#F1DEDA]">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    <Textarea value={trActive ? ((im.tr?.[lang]?.caption) ?? "") : (im.caption || "")} disabled={ro} onChange={(e) => { const v = e.target.value; if (trActive) updateDocImage(im.id, { tr: { ...(im.tr || {}), [lang]: { ...(im.tr?.[lang] || {}), caption: v } }, _trsrc: lang }); else updateDocImage(im.id, { caption: v, _trsrc: "da" }); }}
                      placeholder={t("image_caption_ph")} className="w-full mt-2 border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[64px] text-sm" data-testid="doc-image-caption" />
                  </div>
                ))}
                {canEdit && (
                  <>
                    <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="doc-image-capture" onChange={(e) => { uploadDocImage(e.target.files?.[0]); e.target.value = ""; }} />
                    <input ref={driveInputRef} type="file" accept="image/*,application/pdf,.pdf" className="hidden" data-testid="doc-image-upload" onChange={(e) => { uploadDocImage(e.target.files?.[0]); e.target.value = ""; }} />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                <button type="button" className="no-print inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#EBE8E3] bg-white text-sm font-medium text-stone-700 cursor-pointer hover:bg-[#F3F1EC] w-fit" data-testid="doc-add-image-btn">
                          <Upload className="h-4 w-4 text-[#A83232]" strokeWidth={2.25} /> {t("add_image")}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-52">
                        <DropdownMenuItem data-testid="doc-add-image-camera" onSelect={() => setTimeout(() => cameraInputRef.current?.click(), 0)}>
                          <Camera className="h-4 w-4 mr-2 text-stone-500" /> {t("take_photo")}
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="doc-add-image-drive" onSelect={() => setTimeout(() => driveInputRef.current?.click(), 0)}>
                          <HardDrive className="h-4 w-4 mr-2 text-stone-500" /> {t("upload_drive")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-[#F3F1EC]">
                <DefectsBlock defects={report.doc_defects || []} ro={ro} onChange={setDocDefects} t={t} lang={lang} trActive={trActive} />
              </div>
            </section>

            <section data-testid="doc-section-konklusion">
              <h3 className="text-lg font-semibold text-[#1A1919] mb-3">{t("conclusion")}</h3>
              <div className="no-print flex gap-3 mb-3">
                {[{ key: "I gang", color: "#A08979", tid: "igang", Icon: Loader2 }, { key: "Færdig", color: "#7E856C", tid: "faerdig", Icon: CheckCircle2 }].map((o) => (
                  <button key={o.key} disabled={ro} data-testid={`doc-conclusion-${o.tid}`} onClick={() => chooseStatus(o.key)}
                    className="flex-1 py-3 rounded-xl border font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                    style={report.status === o.key ? { background: o.color, color: "#fff", borderColor: "transparent" } : { background: "#fff", color: "#57534E", borderColor: "#EBE8E3" }}>
                    <o.Icon className="h-4 w-4" /> {t(`status_${o.key}`)}
                  </button>
                ))}
              </div>
              <Textarea value={fieldVal("conclusion_note")} disabled={ro} onChange={(e) => setField("conclusion_note", e.target.value)} placeholder={t("conclusion_note_ph")} className="border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[70px]" data-testid="doc-conclusion-note" />
            </section>
          </div>
        </div>

        <aside className="no-print hidden lg:block lg:sticky lg:top-[84px] space-y-4">
          {renderPanel("")}
        </aside>
      </div>

      <Dialog open={!!editImg} onOpenChange={(o) => !o && setEditImg(null)}>
        <DialogContent className="max-w-3xl" data-testid="doc-annotator-dialog" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t("annot_title")}</DialogTitle>
            <DialogDescription>{t("annot_desc")}</DialogDescription>
          </DialogHeader>
          {editingImage?.image_id && (
            <ImageAnnotator src={imgUrl(editingImage.image_id)} value={editingImage.annotations || []}
              onChange={(annotations) => updateDocImage(editingImage.id, { annotations })} />
          )}
          <div className="flex justify-end pt-2">
            <Button onClick={() => setEditImg(null)} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="doc-annotator-done"><Check className="h-4 w-4 mr-1.5" /> {t("done")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="doc-share-dialog" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t("share")}</DialogTitle>
            <DialogDescription>{t("share_dialog_desc")}</DialogDescription>
          </DialogHeader>
          {report.share_token ? (
            <div className="space-y-4 py-1">
              <div className="flex gap-2" dir="ltr">
                <Input readOnly value={shareUrl} data-testid="doc-share-url" className="rounded-xl bg-[#FBFAF8] border-[#EBE8E3] text-sm" onFocus={(e) => e.target.select()} />
                <Button onClick={copyShare} data-testid="doc-share-copy" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a] shrink-0"><Copy className="h-4 w-4 mr-1.5" /> {t("copy")}</Button>
              </div>
              <ShareExpiry kind="report" docId={id} expiresAt={report?.share_expires_at} onChange={(v) => setReport((p) => ({ ...p, share_expires_at: v }))} disabled={ro} />
              <div className="flex items-center justify-between">
                <a href={report?.share_token ? `${window.location.origin}/kvalitetsrapport-kunde/${report.share_token}` : shareUrl} target="_blank" rel="noreferrer" className="text-sm text-[#A83232] hover:underline inline-flex items-center gap-1.5" data-testid="doc-share-open"><ExternalLink className="h-4 w-4" /> {t("open_customer_view")}</a>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={regenShare} className="rounded-xl text-stone-600" data-testid="doc-share-regen"><RefreshCw className="h-4 w-4 mr-1.5" /> {t("new_link")}</Button>
                  <Button variant="ghost" size="sm" onClick={deleteShare} className="rounded-xl text-[#A83232] hover:bg-[#F1DEDA]" data-testid="doc-share-delete"><Trash2 className="h-4 w-4 mr-1.5" /> {t("deactivate")}</Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-4 text-center">
              <Button onClick={createShare} data-testid="doc-share-create" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]"><Share2 className="h-4 w-4 mr-1.5" /> {t("create_link")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={revOpen} onOpenChange={(o) => { setRevOpen(o); if (!o) setRevNote(""); }}>
        <DialogContent data-testid="doc-revision-dialog" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t("new_version")}</DialogTitle>
            <DialogDescription>{t("rev_desc")}</DialogDescription>
          </DialogHeader>
          <Textarea value={revNote} onChange={(e) => setRevNote(e.target.value)} placeholder={t("rev_note_ph")} className="border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[90px]" data-testid="doc-revision-note" autoFocus />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" className="rounded-xl" onClick={() => setRevOpen(false)}>{t("cancel")}</Button>
            <Button onClick={bumpRevision} disabled={!revNote.trim()} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="doc-revision-confirm"><Plus className="h-4 w-4 mr-1.5" /> {t("create_version")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent dir={dir}>
          <AlertDialogHeader><AlertDialogTitle>{t("delete_document_q")}</AlertDialogTitle><AlertDialogDescription>{t("action_undone")}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="doc-delete-confirm" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={verNotice} onOpenChange={(o) => { if (!o) ackVerNotice(); }}>
        <AlertDialogContent dir={dir} data-testid="doc-version-notice">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#A83232]">{t("ver_notice_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("ver_notice_body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={ackVerNotice} data-testid="doc-version-notice-ok" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">{t("ver_notice_ok")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, children }) => (<div className="space-y-1.5"><label className="label-caps">{label}</label>{children}</div>);

const MetaItem = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-400 mb-1">{label}</p>
    <p className="text-sm font-medium text-[#1A1919] truncate">{value || "—"}</p>
  </div>
);

function ConclusionBadge({ report, t }) {
  const map = {
    "Færdig": { c: "#7E856C", bg: "#E7EADF", bd: "#D3D8C6", Icon: CheckCircle2 },
    "I gang": { c: "#806B61", bg: "#F4EFEC", bd: "#E6DDD6", Icon: Loader2 },
    "Indført på stykliste": { c: "#A83232", bg: "#F1DEDA", bd: "#E8C9C4", Icon: ListChecks },
    Kladde: { c: "#78716C", bg: "#F3F1EC", bd: "#E7E5E4", Icon: null },
  };
  const s = map[report.status] || map.Kladde;
  return (
    <span data-testid="doc-conclusion-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: s.bg, color: s.c, border: `1px solid ${s.bd}` }}>
      {s.Icon && <s.Icon className="h-3.5 w-3.5" />}{t(`status_${report.status || "Kladde"}`)}
    </span>
  );
}

function DefectsBlock({ defects = [], ro, onChange, t, lang, trActive }) {
  const add = () => onChange([...defects, { id: genId(), description: "", level: "Mindre" }]);
  const upd = (did, patch) => onChange(defects.map((d) => d.id === did ? { ...d, ...patch } : d));
  const rm = (did) => onChange(defects.filter((d) => d.id !== did));
  return (
    <div>
      <div className="flex items-center gap-2 mb-3"><ListChecks className="h-4 w-4 text-[#A83232]" /><span className="font-semibold text-[#1A1919]">{t("remarks")}</span></div>
      <div className="space-y-2">
        {defects.map((d) => {
          const lc = LEVELS.find((l) => l.key === d.level)?.color || "#A8A29E";
          return (
            <div key={d.id} className="flex items-center gap-2" data-testid="doc-defect-row">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: lc }} />
              <Input value={trActive ? ((d.tr?.[lang]?.description) ?? "") : (d.description || "")} disabled={ro} onChange={(e) => { const v = e.target.value; if (trActive) upd(d.id, { tr: { ...(d.tr || {}), [lang]: { ...(d.tr?.[lang] || {}), description: v } }, _trsrc: lang }); else upd(d.id, { description: v, _trsrc: "da" }); }} placeholder={t("remark_desc_ph")} className="rounded-lg bg-white border-[#EBE8E3] flex-1" data-testid="doc-defect-desc" />
              <Select value={d.level} disabled={ro} onValueChange={(v) => upd(d.id, { level: v })}>
                <SelectTrigger className="w-32 rounded-lg bg-white border-[#EBE8E3]" data-testid="doc-defect-level"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l.key} value={l.key}>{t(`level_${l.key}`)}</SelectItem>)}</SelectContent>
              </Select>
              {!ro && <button data-testid="doc-defect-remove" onClick={() => rm(d.id)} className="p-1.5 text-[#A83232] hover:bg-[#F1DEDA] rounded-md"><Trash2 className="h-4 w-4" /></button>}
            </div>
          );
        })}
        {defects.length === 0 && <p className="text-sm text-stone-400">{t("no_remarks")}</p>}
      </div>
      {!ro && <Button variant="ghost" size="sm" onClick={add} className="mt-2 rounded-lg text-[#A83232] hover:bg-[#F1DEDA]" data-testid="doc-add-defect"><Plus className="h-4 w-4 mr-1" /> {t("add_remark")}</Button>}
    </div>
  );
}
